import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Trans, withI18n, withI18nProps } from '@lingui/react'
import compose from 'lodash/fp/compose'

import prepareComponent from 'client/shared/decorators/prepare-component'
import errorBoundary from 'client/shared/decorators/error-boundary'

import { buildUser, loadSeries } from 'client/bookmate/reducers/user-reducer'

import { getUserSeries } from 'client/bookmate/selectors/user-selectors'

import UserSubHeaderBox from 'client/bookmate/boxes/user-sub-header-box'
import UserSubHeaderBooksBox from 'client/bookmate/boxes/user-sub-header-books-box'
import HeaderBox from 'client/shared/boxes/header-box'
import UserInfoBox from 'client/bookmate/boxes/user-info-box'

import MetaTitle from 'client/shared/blocks/meta-title'
import { MetaDescription } from 'client/shared/blocks/meta-description'
import { Heading } from 'client/shared/blocks/heading'
import { LayoutColumn } from 'client/shared/blocks/layout-column'
import Spacer from 'client/shared/blocks/spacer'
import List from 'client/bookmate/blocks/list'
import Series from 'client/shared/blocks/series'
import InfiniteScroll from 'client/shared/blocks/infinite-scroll'

import { Dispatch, State } from 'shared/types/redux'
import { SeriesProps } from 'client/shared/types/series'
import { UserLong } from 'client/shared/types/user'
import { Params } from 'client/shared/types/react-router'

type Props = {
  user: UserLong
  currentUserLogin: string
  seriesList: SeriesProps[]
  dispatch: Dispatch
  params: Params
} & withI18nProps

class UserSeriesBox extends Component<Props> {
  load = () => {
    const {
      dispatch,
      user: { login, pages },
    } = this.props

    if (login) {
      return dispatch(loadSeries(login, pages.series, 20, true))
    }
  }

  render() {
    const {
      user,
      user: { myself },
    } = this.props

    return (
      <>
        {this.renderMeta()}
        <HeaderBox />
        {myself ? <UserSubHeaderBooksBox /> : <UserInfoBox user={user} />}
        <UserSubHeaderBox hidden={myself} />
        <Spacer size={32} />
        <LayoutColumn>
          <Heading looksLike={2}>
            <Trans id={`profile.series`} />
          </Heading>
          {this.renderSeries()}
          <Spacer size="bottom-page" />
        </LayoutColumn>
      </>
    )
  }

  renderMeta() {
    const {
      user: { name, login: username },
      i18n,
    } = this.props

    const title = i18n._('profile.meta_title_series', { name, username })
    const description = i18n._('profile.meta_description_series', {
      name,
      username,
    })

    return (
      <>
        <MetaTitle title={title} />
        <MetaDescription description={description} />
      </>
    )
  }

  renderSeries() {
    const {
      seriesList,
      user: { pages },
      params: { filter },
    } = this.props

    const shelvesPage = pages.shelves[filter]

    return (
      <InfiniteScroll load={this.load} page={shelvesPage}>
        <List>
          {seriesList.map((series, i) => (
            <Series series={series} key={series.uuid || i} />
          ))}
        </List>
      </InfiniteScroll>
    )
  }
}

const prepareWrapper = prepareComponent(
  async function ({ store, params }) {
    const { username } = params
    const { dispatch } = store

    await dispatch(loadSeries(username))
  },
  {
    userListener: true,
  },
)

const connectWrapper = connect((state: State) => ({
  user: buildUser(state, state.user.login),
  seriesList: getUserSeries(state),
  currentUserLogin: state.currentUser.data.login,
}))

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  errorBoundary,
  withI18n({ update: true }),
)

export default wrappers(UserSeriesBox)
